import {
  EmptyState,
  ListX,
  Table,
  TableProps,
  TableReloadHandle,
  TableSortOption,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import React, { useRef, useState } from "react";
import { EMAIL_CYCLES_PER_PAGE } from "../../stores/EmailCyclesStore";
import { useTranslation } from "react-i18next";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { TypesSortOrder } from "@pulse/pulse-rpcs/lib/types/typesSortOrder";
import { GetEmailCyclesRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailCyclesRPC";
import { getSortOrder } from "../../../utils/getSortOrder";
import { observer } from "mobx-react";
import { useEmailCyclesStore } from "../../stores/Hooks";
import {
  processProjectParams,
  useRouteNavigator,
} from "../../../../routes/RoutesHelper";
import { EmailCycleStatus } from "@pulse/pulse-rpcs/lib/email-cycle/emailCycleStatus";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import {
  getEmailCyclesTableCells,
  getEmailCyclesTableHeader,
} from "./headersAndCells/EmailCycleTableHeadersAndCells";
import { useStopEmailCycleStore } from "../../stores/Hooks";
import { StopEmailCycleDialog } from "../dialogs/StopEmailCycleDialog";
import { useSignInStore } from "../../../auth/store/hook";
import { UserPrivileges } from "../../../store/user/UserPrivileges";

interface EmailCyclesTableProps {
  navigateToSurveyTab: VoidFunction;
}

type DialogToOpen = "STOP_EMAIL_CYCLE";

enum EmailCyclesTableColumn {
  CycleName = "CYCLE_NAME",
  Total = "TOTAL",
  Queued = "QUEUED",
  Sent = "SENT",
  Delivered = "DELIVERED",
  Viewed = "VIEWED",
  Unsent = "UNSENT",
  Bounces = "BOUNCES",
  Status = "STATUS",
  QueuedOn = "QUEUED_ON",
  LastModified = "LAST_MODIFIED",
  Creator = "CREATOR",
  Actions = "ACTIONS",
}

interface SortOrderAndColumn {
  order: TypesSortOrder.TypesSortOrder;
  column: GetEmailCyclesRPC.RequestEnums.SortColumn.SortColumn;
}

export const EmailCyclesTable: React.FC<EmailCyclesTableProps> = observer(
  ({ navigateToSurveyTab }) => {
    const [openUnexpectedErrorDialog, setOpenUnexpectedErrorDialog] =
      useState(false);
    const [dialogToOpen, setDialogToOpen] = useState<DialogToOpen>();

    const { t } = useTranslation();
    const navigateTo = useRouteNavigator();
    const typography = useTypography();
    const emailCyclesTableRef = useRef<TableReloadHandle | null>(null);

    const emailCyclesStore = useEmailCyclesStore();
    const stopEmailCycleStore = useStopEmailCycleStore();
    const signInStore = useSignInStore();

    const paginationOption: TableProps["paginationOption"] = {
      itemsPerPage: EMAIL_CYCLES_PER_PAGE,
      getPageIndicatorText: (startItem, endItem, totalItems): string =>
        t("common.paginationIndicationText", {
          startItem,
          endItem,
          totalItems,
        }),
    };

    const viewOverrides: TableProps["viewOverrides"] = {
      empty: {
        icon: (
          <EmptyState
            icon={<ListX />}
            title={t("emailCycles.emptyEmailCycleMessage")}
            description={EMPTY_CHARACTER}
            width="100%"
          />
        ),
        message: EMPTY_CHARACTER,
      },
      loading: {
        message: t("common.searchTableLoadingState"),
      },
    };

    const isProcessEmailCyclePrivilegeGranted = signInStore.isPrivilegeGranted(
      UserPrivileges.processEmailCycle,
    );
    const rightPinnedColumnIds =
      isProcessEmailCyclePrivilegeGranted &&
      emailCyclesStore.emailCycles.some(
        (emailCycle) =>
          emailCycle.status === EmailCycleStatus.EmailCycleStatus.IN_PROGRESS,
      )
        ? [EmailCyclesTableColumn.Actions]
        : [];

    const getSortColumn = (columnId: string): SortOrderAndColumn["column"] => {
      switch (columnId) {
        case EmailCyclesTableColumn.QueuedOn:
          return GetEmailCyclesRPC.RequestEnums.SortColumn.SortColumn
            .CREATED_AT;
        case EmailCyclesTableColumn.LastModified:
          return GetEmailCyclesRPC.RequestEnums.SortColumn.SortColumn
            .MODIFIED_AT;
        default:
          throw new Error(`Invalid columnId: ${columnId}`);
      }
    };

    const getSortOrderAndColumn = (
      sort: TableSortOption | undefined,
    ): SortOrderAndColumn => {
      if (!sort) {
        return {
          order: TypesSortOrder.TypesSortOrder.DESCENDING,
          column:
            GetEmailCyclesRPC.RequestEnums.SortColumn.SortColumn.MODIFIED_AT,
        };
      }

      return {
        order: getSortOrder(sort.order),
        column: getSortColumn(sort.id),
      };
    };

    const onTableOptionsChange: TableProps["onTableOptionsChange"] = async (
      options,
      setTotalItems,
    ) => {
      const { order, column } = getSortOrderAndColumn(options.sort);
      const pageIndex = options.pageNumber ?? 1;

      await processProjectParams(async (projectId) => {
        await emailCyclesStore.getEmailCycles(
          projectId,
          pageIndex,
          order,
          column,
          true, // loadingOptionsChange
        );
      }, navigateTo).catch(() => setOpenUnexpectedErrorDialog(true));

      if (!emailCyclesStore.getEmailCyclesRPCError) {
        setTotalItems(emailCyclesStore.totalItems);
        return emailCyclesStore.emailCycles.map((emailCycle) => ({
          id: emailCycle.id,
          cells: getEmailCyclesTableCells(
            t,
            typography,
            emailCycle,
            (cycleId): void => {
              stopEmailCycleStore.setScheduleId(cycleId);
              setDialogToOpen("STOP_EMAIL_CYCLE");
            },
            isProcessEmailCyclePrivilegeGranted,
          ),
        }));
      }

      switch (emailCyclesStore.getEmailCyclesRPCError) {
        case GetEmailCyclesRPC.RPCError.InvalidProjectId:
          navigateTo.pageNotFound();
          break;
        case GetEmailCyclesRPC.RPCError.InvalidPageIndex:
          await processProjectParams(async (projectId) => {
            await emailCyclesStore.getEmailCycles(
              projectId,
              Math.max(1, pageIndex - 1),
              order,
              column,
              true, // loadingOptionsChange
            );
          }, navigateTo);
          break;
        default:
          emailCyclesStore.getEmailCyclesRPCError satisfies never;
      }

      return [];
    };

    return (
      <>
        <Table
          name="emailCyclesTable"
          ref={emailCyclesTableRef}
          header={getEmailCyclesTableHeader(t)}
          paginationOption={paginationOption}
          onTableOptionsChange={onTableOptionsChange}
          viewOverrides={viewOverrides}
          onRowClick={({ id }) =>
            processProjectParams(
              async (projectId) => navigateTo.emailCycleDetails(projectId, id),
              navigateTo,
            )
          }
          leftPinnedColumnIds={[]}
          rightPinnedColumnIds={rightPinnedColumnIds}
        />
        {openUnexpectedErrorDialog && (
          <UnexpectedErrorDialog
            message={t("emailCycles.getEmailCyclesUnexpectedErrorMessage")}
            onClickDone={navigateToSurveyTab}
          />
        )}
        {dialogToOpen === "STOP_EMAIL_CYCLE" && (
          <StopEmailCycleDialog
            handleDialogClose={(refetchTable = true): void => {
              setDialogToOpen(undefined);
              if (refetchTable) emailCyclesTableRef.current?.reload();
            }}
          />
        )}
      </>
    );
  },
);
